import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function NVCPrimer() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">Nonviolent Communication Primer</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 63</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12"><Sparkles className="h-20 w-20 text-purple-400 mx-auto mb-6" /><h2 className="text-4xl font-bold text-white mb-4">Introduction to Nonviolent Communication</h2></div>
          
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-700/30 rounded-lg p-8">
              <p className="text-blue-100 leading-relaxed text-lg">
                Nonviolent Communication (NVC) has been described as <span className="font-semibold text-white">a language of compassion</span>, as a tool for positive social change. NVC gives us the tools to understand what triggers us, to take responsibility for our reactions, and to deepen our connection with ourselves and others, thereby transforming our habitual responses to life.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-4">The Fundamental Principle</h3>
              <p className="text-purple-100 leading-relaxed text-lg">
                Underlying all human actions are needs that people are seeking to meet. Understanding and acknowledging these needs can create a shared basis for connection, cooperation, and more harmonious relationships on both a personal and global level.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-4">The Power of Focusing on Needs</h3>
              <p className="text-green-100 leading-relaxed">
                When we focus on needs—without interpreting or conveying criticism, blame, or demands—our deeper creativity flourishes, and solutions arise that were previously blocked from our awareness. At this depth, conflicts and misunderstandings can be resolved with greater ease.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-700/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Two Parts, Four Components</h3>
              <p className="text-amber-100 leading-relaxed mb-4">
                The language of NVC includes two parts: <span className="font-semibold">honestly expressing ourselves</span> to others, and <span className="font-semibold">empathically hearing</span> others.
              </p>
              <p className="text-amber-100 leading-relaxed">
                Both are expressed through four components: <span className="text-blue-300">observations</span>, <span className="text-red-300">feelings</span>, <span className="text-green-300">needs</span>, and <span className="text-yellow-300">requests</span>—though observations and requests may or may not be articulated.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-900/20 to-pink-900/20 border border-rose-700/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-4">The Practice</h3>
              <p className="text-rose-100 leading-relaxed">
                Practicing NVC involves distinguishing these components from judgments, interpretations, and demands, and learning to embody the consciousness embedded in these components. This compassionate approach allows us to express ourselves and hear ourselves and others in ways more likely to foster understanding and connection.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-4">The Result</h3>
              <p className="text-cyan-100 leading-relaxed text-lg">
                It allows us to support everyone involved in getting their needs met, and to nurture in all of us <span className="font-semibold text-white">a joy in giving and in receiving</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
